import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import api from '../../api/axios';

interface CaseActionTool {
  title: string;
  description: string;
  enabled: boolean;
  icon: React.ReactNode;
  actionLabel: string;
  onAction?: () => void;
  loading?: boolean;
}

interface CaseActionSectionProps {
  schemeId: string | null;
  ccn: string | undefined;
}

const CaseActionSection: React.FC<CaseActionSectionProps> = ({ schemeId, ccn }) => {
  const [supplementGenerating, setSupplementGenerating] = useState(false);
  const [analysisGenerating, setAnalysisGenerating] = useState(false);
  const [complaintGenerating, setComplaintGenerating] = useState(false);
  const [memoGenerating, setMemoGenerating] = useState(false);
  const [expertReportGenerating, setExpertReportGenerating] = useState(false);

  const downloadDocFromGroupEndpoint = async (
    urlPath: string,
    fallbackName: string,
    setBusy: (b: boolean) => void,
  ) => {
    if (!schemeId || !ccn) return;
    setBusy(true);
    try {
      // scope=facility → single-facility doc (1 row per table). Without
      // this flag, the backend would resolve the chain and emit a doc
      // covering every facility in the owner group.
      const res = await api.post(
        `/schemes/${schemeId}/${urlPath}/`,
        { ccn, scope: 'facility' },
        { responseType: 'blob' },
      );
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : fallbackName;
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Failed to generate ${urlPath}:`, err);
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateComplaint = () =>
    downloadDocFromGroupEndpoint(
      'generate-owner-group-complaint',
      `Understaffing_Complaint_${ccn}.docx`,
      setComplaintGenerating,
    );

  const handleGenerateProsecutionMemo = () =>
    downloadDocFromGroupEndpoint(
      'generate-owner-group-prosecution-memo',
      `Prosecution_Memo_${ccn}.docx`,
      setMemoGenerating,
    );

  const handleGenerateExpertReport = () =>
    downloadDocFromGroupEndpoint(
      'generate-expert-report',
      `Expert_Report_${ccn}.docx`,
      setExpertReportGenerating,
    );

  const handleGenerateSupplement = async () => {
    if (!schemeId || !ccn) return;
    setSupplementGenerating(true);
    try {
      const res = await api.post(
        `/schemes/${schemeId}/generate-investigative-supplement/`,
        { ccn },
        { responseType: 'blob' },
      );
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `Investigative_Supplement_${ccn}.docx`;
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to generate investigative supplement:', err);
    } finally {
      setSupplementGenerating(false);
    }
  };

  const handleGenerateAnalysis = async () => {
    if (!schemeId || !ccn) return;
    setAnalysisGenerating(true);
    try {
      const res = await api.post(`/schemes/${schemeId}/generate-cost-report-analysis/`, { ccn });
      if (res.data?.task_id) {
        const taskId = res.data.task_id;
        const poll = setInterval(async () => {
          try {
            const statusRes = await api.get(`/schemes/${schemeId}/cost-report-analysis-status/?task_id=${taskId}`);
            if (statusRes.data?.state === 'SUCCESS') {
              clearInterval(poll);
              setAnalysisGenerating(false);
              if (statusRes.data?.download_url) {
                const dlRes = await api.get(statusRes.data.download_url, { responseType: 'blob' });
                const contentType = dlRes.headers['content-type'] || '';
                if (contentType.includes('application/json')) {
                  const text = await (dlRes.data as Blob).text();
                  const json = JSON.parse(text);
                  if (json.url) window.open(json.url, '_blank');
                } else {
                  const disposition = dlRes.headers['content-disposition'] || '';
                  const match = disposition.match(/filename="?([^"]+)"?/);
                  const filename = match ? match[1] : `cost_report_analysis_${ccn}.zip`;
                  const blob = new Blob([dlRes.data]);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename;
                  a.click();
                  URL.revokeObjectURL(url);
                }
              }
            } else if (statusRes.data?.state === 'FAILURE') {
              clearInterval(poll);
              setAnalysisGenerating(false);
            }
          } catch {
            clearInterval(poll);
            setAnalysisGenerating(false);
          }
        }, 3000);
      }
    } catch (err) {
      console.error('Failed to generate cost report analysis:', err);
      setAnalysisGenerating(false);
    }
  };

  const tools: CaseActionTool[] = [
    {
      title: 'FCA Complaint',
      description: 'Owner-group-scoped complaint with the core data tables (days operating, state-min/RN-coverage failures, related-party entities) populated from platform data.',
      enabled: true,
      icon: <DescriptionIcon />,
      actionLabel: 'Generate',
      onAction: handleGenerateComplaint,
      loading: complaintGenerating,
    },
    {
      title: 'Prosecution Memo',
      description: 'Owner-group-scoped prosecution memo with facility identification, acuity/state-min compliance, related-party totals and matrix, citations/penalties, and RN-LPN wage anomalies.',
      enabled: true,
      icon: <AssessmentIcon />,
      actionLabel: 'Generate',
      onAction: handleGenerateProsecutionMemo,
      loading: memoGenerating,
    },
    {
      title: 'First CID',
      description: 'Draft the initial Civil Investigative Demand (CID) for the facility.',
      enabled: false,
      icon: <ScheduleIcon />,
      actionLabel: 'Coming Soon',
    },
    {
      title: 'Expert Report',
      description: 'Nurse-staffing expert report (Halifax format) with state-minimum compliance, PBJ-vs-acuity (Harrington/Schnelle) HPRD comparison, and days/value of understaffing tables populated from platform data.',
      enabled: true,
      icon: <AssessmentIcon />,
      actionLabel: 'Generate',
      onAction: handleGenerateExpertReport,
      loading: expertReportGenerating,
    },
    /* Investigative Supplement hidden + disabled "Coming Soon" tools removed (Financials and Staffing Analysis, Punch Detail Analysis, Medicaid Claims Data Analysis)
    {
      title: 'Investigative Supplement',
      description: 'Auto-generate the Case Action memo with staffing records requests, claims data references, and ownership/affiliated persons \u2014 populated from platform data.',
      enabled: true,
      icon: <DescriptionIcon />,
      actionLabel: 'Generate',
      onAction: handleGenerateSupplement,
      loading: supplementGenerating,
    },
    {
      title: 'Financials and Staffing Analysis',
      description: 'Generate financial and staffing analysis workbook from HCRIS cost report data.',
      enabled: false,
      icon: <AssessmentIcon />,
      actionLabel: 'Coming Soon',
    },
    {
      title: 'Punch Detail Analysis',
      description: 'Upload and analyze facility punch detail data against PBJ submissions.',
      enabled: false,
      icon: <ScheduleIcon />,
      actionLabel: 'Coming Soon',
    },
    {
      title: 'Medicaid Claims Data Analysis',
      description: 'Match understaffed days against Medicaid claims to quantify billing on days below acuity.',
      enabled: false,
      icon: <ScheduleIcon />,
      actionLabel: 'Coming Soon',
    },
    */
  ];

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Case Action
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generate investigative documents and run case-specific analyses.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {tools.map((tool) => (
          <Paper
            key={tool.title}
            variant="outlined"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderRadius: 2,
              opacity: tool.enabled ? 1 : 0.45,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1, minWidth: 0 }}>
              <Box sx={{ color: tool.enabled ? 'text.primary' : 'text.disabled', mt: 0.25 }}>
                {tool.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={600} color={tool.enabled ? 'text.primary' : 'text.disabled'}>
                  {tool.title}
                </Typography>
                <Typography variant="body2" color={tool.enabled ? 'text.secondary' : 'text.disabled'}>
                  {tool.description}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ ml: 3, flexShrink: 0 }}>
              {tool.enabled ? (
                <Button
                  variant="contained"
                  size="small"
                  disabled={tool.loading}
                  startIcon={tool.loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                  onClick={tool.onAction}
                  sx={{ minWidth: 100 }}
                >
                  {tool.loading ? 'Generating...' : tool.actionLabel}
                </Button>
              ) : (
                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                  {tool.actionLabel}
                </Typography>
              )}
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default CaseActionSection;
